import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Input } from '@wds/input';
import { Modal } from '@wds/modal';
import { Typography } from '@wds/typography';
import { ProgressCircle } from '@wds/progress-circle';

import { getCookie } from 'sma.common/utils';
import STRINGS from '../Authentication/constants';
import MFAErrorMessage from '../Authentication/components/MFAErrorMessage';
import { clearCooldownTimer, startCooldownTimer } from '../Authentication/utils/cooldown';

const RESEND_COOLDOWN_SEC = 120;

const ChangeRecoveryEmailModal = (props) => {
    const { open, handleClose, mfaMethod, primaryEmail, onSuccess, currentMaskedEmail } = props;

    // State
    const [step, setStep] = useState(1);
    const [code, setCode] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [error, setError] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [newMaskedEmail, setNewMaskedEmail] = useState('');
    const [isLockedOut, setIsLockedOut] = useState(false);
    const timerRef = useRef(null);
    const cooldownEndAtRef = useRef(0);

    // Initial Setup
    useEffect(() => {
        if (open) {
            setStep(1);
            setCode('');
            setNewEmail('');
            setError('');
            setIsLockedOut(false);
            
            // Track when recovery email change is started
            if (window.track) {
                window.track('mfaRecoveryEmailChangeStarted', {
                    hasExistingRecoveryEmail: !!currentMaskedEmail,
                    mfaMethod: mfaMethod
                });
            }
            
            // If email MFA is required initially, trigger it
            if (mfaMethod === 'email') handleTriggerChallenge('mfa');
        }
    }, [open, mfaMethod]);

    useEffect(() => {
        return () => {
            clearCooldownTimer(timerRef);
        };
    }, []);

    // Clear cooldown timer when locked out
    useEffect(() => {
        if (isLockedOut) {
            clearCooldownTimer(timerRef);
            setCooldown(0);
        }
    }, [isLockedOut]);

    const startCooldown = (remaining) => {
        startCooldownTimer({
            remaining,
            defaultSeconds: RESEND_COOLDOWN_SEC,
            setCooldown,
            timerRef,
            endAtRef: cooldownEndAtRef,
        });
    };

    // Clear "code already pending" message when cooldown expires
    useEffect(() => {
        if (cooldown === 0 && error === 'code_already_pending') {
            setError('');
        }
    }, [cooldown]);

    const activateLockoutState = () => {
        setIsLockedOut(true);
        setError('lockedOutMessage');
    };

    // Unified API Call Logic
    const handleTriggerChallenge = async (type) => {
        setSendingCode(true);
        setError('');
        try {
            const isMfa = type === 'mfa';
            const url = isMfa ? reverse('api-internal-mfa-challenge') : reverse('api-internal-mfa-recovery-email');
            const body = isMfa 
                ? { action: 'send_code', method: 'email', context: 'recovery_email_change' } 
                : { action: 'setup_recovery_email', email: newEmail };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') },
                body: JSON.stringify(body),
            });
            let data = {};
            try {
                data = await response.json();
            } catch (e) {
                data = {};
            }

            if (
                response.status === 401 ||
                data.error === 'invalid_code_locked_out' ||
                data.error === 'not_authenticated'
            ) {
                activateLockoutState();
                return;
            }
            
            // Handle cooldown case - show error with cooldown timer
            if (data.error === 'code_already_pending' && data.time_remaining) {
                setError(data.error);
                startCooldown(data.time_remaining);
                // For recovery email, still move to step 3 so user can enter the code
                if (type === 'recovery') {
                    setNewMaskedEmail(newEmail);
                    setStep(3);
                    setCode('');
                }
            } else if (data.error === 'rate_limited') {
                activateLockoutState();
            } else if (data.success) {
                if (type === 'recovery') {
                    setNewMaskedEmail(newEmail);
                    setStep(3);
                    setCode('');
                    // Track recovery email code sent
                    if (window.track) {
                        window.track('mfaRecoveryEmailCodeSent', {
                            setupContext: 'change',
                            isResend: false,
                            email: newEmail
                        });
                    }
                }
                if (data.time_remaining) {
                    startCooldown(data.time_remaining);
                } else {
                    startCooldown();
                }
            } else {
                setError(data.error || STRINGS.failedToSendCode);
            }
        } catch (err) { setError(STRINGS.unknownError); }
        finally { setSendingCode(false); }
    };

    // Unified Button Action
    const handlePrimaryAction = async () => {
        // Step 2: Send verification to new email
        if (step === 2) {
            await handleTriggerChallenge('recovery');
            return;
        }

        // Validate Code Length
        if (code.length !== 6) return;

        setVerifying(true);
        setError('');
        try {
            const isStep1 = step === 1;
            const url = isStep1 ? reverse('api-internal-mfa-challenge') : reverse('api-internal-mfa-recovery-email');
            const action = isStep1 
                ? (mfaMethod === 'totp' ? 'verify_totp_challenge' : 'verify_email_challenge') 
                : 'verify_recovery_email';

            const requestBody = isStep1 
                ? { action, challenge_code: code, method: mfaMethod, context: 'recovery_email_change' }
                : { action, code };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCookie('csrftoken') },
                body: JSON.stringify(requestBody),
            });
            let data = {};
            try {
                data = await response.json();
            } catch (e) {
                data = {};
            }
            
            if (data.success) {
                if (isStep1) setStep(2); // Move to email entry
                else {
                    // Track successful recovery email verification
                    if (window.track) {
                        window.track('mfaRecoveryEmailVerified', {
                            setupContext: 'change',
                            email: newMaskedEmail
                        });
                    }
                    // Track successful completion
                    if (window.track) {
                        window.track('mfaRecoveryEmailChangeCompleted', {
                            oldEmail: currentMaskedEmail,
                            newEmail: newMaskedEmail
                        });
                    }
                    onSuccess(newMaskedEmail); // Complete
                    handleClose();
                }
            } else {
                // Handle lockout/rate limit errors
                if (
                    response.status === 401 ||
                    data.error === 'rate_limited' ||
                    data.error === 'invalid_code_locked_out' ||
                    data.error === 'not_authenticated'
                ) {
                    activateLockoutState();
                    // Track failure
                    if (!isStep1 && window.track) {
                        window.track('mfaRecoveryEmailChangeFailed', {
                            errorType: data.error,
                            step: 'email_verification'
                        });
                    }
                } else {
                    // Track other failures
                    if (!isStep1 && window.track) {
                        window.track('mfaRecoveryEmailChangeFailed', {
                            errorType: data.error || 'invalid_code',
                            step: 'email_verification'
                        });
                    }
                    setError(data.error || STRINGS.invalidCode);
                }
            }
        } catch (err) { setError(STRINGS.unknownError); }
        finally { setVerifying(false); }
    };

    const handleResendCode = (type) => {
        // Track code resend
        if (window.track) {
            window.track('mfaRecoveryEmailCodeSent', {
                setupContext: 'change',
                isResend: true,
                email: type === 'recovery' ? newMaskedEmail : primaryEmail
            });
        }
        handleTriggerChallenge(type);
    };

    // --- TEXT LOGIC RESTORED ---
    const getHeaderText = () => {
        if (step === 2) return currentMaskedEmail ? STRINGS.changeRecoveryEmailAddress : STRINGS.addRecoveryEmailTitle;
        if (step === 3) return STRINGS.enterVerificationCode;
        if (step === 1 && mfaMethod === 'email') return STRINGS.enterVerificationCode;
        return STRINGS.enterOneTimeCodeToContinue;
    };

    const getBodyText = () => {
        if (step === 1) {
            if (mfaMethod === 'totp') {
                return STRINGS.enterCodeFromAuthApp;
            }
            return (
                <>
                    {STRINGS.enterVerificationCodeDescription}
                    {' ('}
                    <span style={{ fontWeight: '700' }}>{primaryEmail}</span>
                    {')'}
                </>
            );
        }
        if (step === 2) {
            return STRINGS.recoveryEmailBackupDescription;
        }
        return (
            <>
                {STRINGS.enterVerificationCodeDescription}
                {' ('}
                <span style={{ fontWeight: '700' }}>{newMaskedEmail}</span>
                {')'}
            </>
        );
    };

    // --- LAYOUT STYLES ---
    const formContainerStyle = {
        width: '100%',
        maxWidth: '440px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch', // Forces Input and Button to match width
    };

    const inputStyle = {
        width: '100%',
        boxSizing: 'border-box',
        padding: '0 16px',
        fontSize: '15px',
        borderRadius: '4px',
        border: '10px solid #26030d',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
    };

    const isReady = step === 2 ? !!newEmail : code.length === 6;
    const isButtonDisabled = verifying || !isReady || isLockedOut;

    const handleModalClose = () => {
        if (isLockedOut) {
            window.location.href = '/mfa/locked-out/';
        } else {
            handleClose();
        }
    };

    return (
        <Modal show={open} onClose={handleModalClose} size="sm">
            <Modal.Body style={{ padding: '56px 40px' }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                     <Box mb={5}>
                        <img src='/media/img/smapply/apply.svg' alt='Apply' style={{ width: '110px' }} />
                    </Box>
                    
                    {/* Container for Text, Input, and Button */}
                    <Box style={formContainerStyle}>
                        
                        {/* Header Text */}
                        <Box mb={4}>
                            <Typography variant="h4" style={{ fontSize: '22px', fontWeight: '700', marginBottom: '12px', color: '#111827', textAlign: 'left' }}>
                                {getHeaderText()}
                            </Typography>
                            <Typography style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6', textAlign: 'left' }}>
                                {getBodyText()}
                            </Typography>
                        </Box>


                        {/* Input Field */}
                        <Box display='flex'
                                    flexDirection='column'
                                    gap={4}
                                    pb={6}>
									<MFAErrorMessage error={error} />
                            {step === 2 ? (
                                <Input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder={STRINGS.enterNewRecoveryEmail}
                                    stretched
                                    disabled={isLockedOut}
                                    style={inputStyle}
                                />
                            ) : (
                                <Input
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder={step === 3 ? STRINGS.enterCode : STRINGS.enterCodeProvided}
                                    stretched
                                    disabled={isLockedOut}
                                    style={inputStyle}
                                />
                            )}
                        </Box>

                        {/* Resend Button */}
                        {(step === 3 || (step === 1 && mfaMethod === 'email')) && (
                            <Box style={{ textAlign: 'right' }} mb={3}>
                                <Typography
                                    component='span'
                                    onClick={(cooldown > 0 || sendingCode || isLockedOut) ? undefined : () => handleResendCode(step === 1 ? 'mfa' : 'recovery')}
                                    style={{
                                        color: (cooldown > 0 || sendingCode || isLockedOut) ? '#768086' : '#007FAA',
                                        cursor: (cooldown > 0 || sendingCode || isLockedOut) ? 'not-allowed' : 'pointer',
                                        fontSize: '15px',
                                        fontWeight: '500'
                                    }}
                                >
                                    {sendingCode
                                        ? STRINGS.sending
                                        : cooldown > 0
                                        ? `${STRINGS.resendCodeWithCountdown} (${cooldown}s)`
                                        : STRINGS.resendCode}
                                </Typography>
                            </Box>
                        )}

                        {/* Full Width Button */}
                        <Button
                            onClick={handlePrimaryAction}
                            disabled={isButtonDisabled}
                            style={{ 
                                width: '100%',
                                boxSizing: 'border-box',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '48px',
                                padding: '0 16px', 
                                backgroundColor: !isButtonDisabled ? '#98D5B0' : '#E5E7EB', 
                                color: 'white', 
                                borderRadius: '4px', 
                                border: 'none', 
                                fontWeight: '700', 
                                fontSize: '15px',
                                cursor: !isButtonDisabled ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {verifying ? <ProgressCircle continuous size="sm" /> : (step === 2 ? STRINGS.verify : STRINGS.continue)}
                        </Button>
                    </Box>
                </Box>
            </Modal.Body>
        </Modal>
    );
};

export default ChangeRecoveryEmailModal;